import { useMemo, useState } from "react";
import { Loader2, Mail, Plus, UserMinus, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { CollaboratorsList } from "./types";
import type { AdminHubspotDealOwner } from "./use-admin-deal";
import { useAddDealCollaborator } from "./use-add-deal-collaborator";
import { useRemoveDealCollaborator } from "./use-remove-deal-collaborator";
import { useEligibleCollaborators } from "./use-eligible-collaborators";

// SPA port of CollaboratorsPanel. Tabs: Bookboost (vendor) + Partner.
// Vendor tab shows the HubSpot deal owner as a read-only pill at the top;
// add-collaborator dialog filters them out of the eligible list. Pattern A
// optimistic add/remove via the Phase 6 mutation hooks.

export function CollaboratorsPanel({
  hubspotDealId,
  collaborators,
  canManage,
  owner,
}: {
  hubspotDealId: string;
  collaborators: CollaboratorsList;
  canManage: boolean;
  owner: AdminHubspotDealOwner | null;
}) {
  const ownerProfileId = owner?.matched?.userId ?? null;
  const vendorAlreadyAddedIds = useMemo(
    () =>
      collaborators.vendor
        .map((c) => (c.side === "vendor" ? c.user_id : null))
        .filter((x): x is string => !!x),
    [collaborators.vendor],
  );
  const vendorExistingIds = useMemo(
    () =>
      ownerProfileId
        ? [...vendorAlreadyAddedIds, ownerProfileId]
        : vendorAlreadyAddedIds,
    [vendorAlreadyAddedIds, ownerProfileId],
  );

  const vendorCount = collaborators.vendor.length + (owner ? 1 : 0);

  return (
    <section
      aria-label="Collaborators"
      className="rounded-lg border bg-card px-4 py-3"
    >
      <header className="mb-3 flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Collaborators
        </p>
      </header>

      <Tabs defaultValue="vendor">
        <TabsList className="h-8 bg-muted/40">
          <TabsTrigger value="vendor" className="text-[11px]">
            Bookboost
            {vendorCount > 0 && (
              <span className="ml-1.5 text-muted-foreground">
                ({vendorCount})
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="partner" className="text-[11px]">
            Partner
            {collaborators.partner.length > 0 && (
              <span className="ml-1.5 text-muted-foreground">
                ({collaborators.partner.length})
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vendor" className="mt-3 space-y-2">
          {owner && <OwnerPill owner={owner} />}
          {collaborators.vendor.length > 0 ? (
            <CollaboratorList
              entries={collaborators.vendor}
              emptyLabel=""
              canManage={canManage}
              hubspotDealId={hubspotDealId}
            />
          ) : !owner ? (
            <p className="rounded-md border border-dashed bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
              No Bookboost staff on this deal yet.
            </p>
          ) : null}
          {canManage && (
            <AddCollaboratorDialog
              hubspotDealId={hubspotDealId}
              side="vendor"
              existingIds={vendorExistingIds}
            />
          )}
        </TabsContent>

        <TabsContent value="partner" className="mt-3">
          <CollaboratorList
            entries={collaborators.partner}
            emptyLabel="No partner users on this deal yet."
            canManage={canManage}
            hubspotDealId={hubspotDealId}
          />
          {canManage && (
            <AddCollaboratorDialog
              hubspotDealId={hubspotDealId}
              side="partner"
              existingIds={collaborators.partner
                .map((c) =>
                  c.side === "partner" ? c.partner_user_id : null,
                )
                .filter((x): x is string => !!x)}
            />
          )}
        </TabsContent>
      </Tabs>
    </section>
  );
}

function CollaboratorList({
  entries,
  emptyLabel,
  canManage,
  hubspotDealId,
}: {
  entries:
    | CollaboratorsList["vendor"]
    | CollaboratorsList["partner"];
  emptyLabel: string;
  canManage: boolean;
  hubspotDealId: string;
}) {
  if (entries.length === 0) {
    if (!emptyLabel) return null;
    return (
      <p className="rounded-md border border-dashed bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
        {emptyLabel}
      </p>
    );
  }

  return (
    <ul className="space-y-1.5">
      {entries.map((c) => (
        <li
          key={c.id}
          className="flex items-center justify-between gap-2 rounded-md border bg-background px-2.5 py-1.5"
        >
          <div className="flex min-w-0 items-center gap-2">
            <Avatar
              name={c.name}
              src={c.side === "vendor" ? c.avatar_url : null}
            />
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-foreground">
                {c.name ?? "(unnamed)"}
              </p>
              {c.email && (
                <p className="flex items-center gap-1 truncate text-[10px] text-muted-foreground">
                  <Mail className="h-2.5 w-2.5" />
                  {c.email}
                </p>
              )}
            </div>
          </div>
          {canManage && (
            <RemoveButton
              hubspotDealId={hubspotDealId}
              collaboratorId={c.id}
              pending={"pending" in c && c.pending === true}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

function OwnerPill({ owner }: { owner: AdminHubspotDealOwner }) {
  const displayName =
    owner.matched?.fullName ||
    [owner.firstName, owner.lastName].filter(Boolean).join(" ").trim() ||
    owner.email ||
    "(unknown owner)";
  const avatarSrc = owner.matched?.avatarUrl ?? null;
  const subtitle = owner.matched
    ? "Owner · HubSpot"
    : owner.email
      ? "HubSpot · not linked to a Bookboost profile"
      : "HubSpot · no email on owner record";

  return (
    <div
      className="flex items-center gap-2 rounded-md border border-accent/30 bg-accent/5 px-2.5 py-1.5"
      aria-label="HubSpot deal owner"
    >
      <Avatar name={displayName} src={avatarSrc} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate text-xs font-medium text-foreground">
          <span className="truncate">{displayName}</span>
          <span className="rounded bg-accent/15 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-accent">
            Owner
          </span>
        </p>
        <p className="truncate text-[10px] text-muted-foreground">
          {subtitle}
          {!owner.matched && owner.email && (
            <span className="ml-1 text-muted-foreground/80">
              · {owner.email}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

function Avatar({ name, src }: { name: string | null; src: string | null }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name ?? ""}
        className="h-6 w-6 rounded-full object-cover"
      />
    );
  }
  const initials = (name ?? "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
      {initials}
    </div>
  );
}

function RemoveButton({
  hubspotDealId,
  collaboratorId,
  pending,
}: {
  hubspotDealId: string;
  collaboratorId: string;
  pending: boolean;
}) {
  const mutation = useRemoveDealCollaborator();
  return (
    <button
      type="button"
      disabled={pending || mutation.isPending}
      onClick={() => mutation.mutate({ hubspotDealId, collaboratorId })}
      className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      title="Remove"
    >
      {pending || mutation.isPending ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <UserMinus className="h-3 w-3" />
      )}
    </button>
  );
}

function AddCollaboratorDialog({
  hubspotDealId,
  side,
  existingIds,
}: {
  hubspotDealId: string;
  side: "vendor" | "partner";
  existingIds: string[];
}) {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const eligible = useEligibleCollaborators(hubspotDealId, open);
  const add = useAddDealCollaborator();

  const candidates =
    side === "vendor" ? eligible.data?.vendors : eligible.data?.partnerUsers;
  const filtered = (candidates ?? []).filter((c) => !existingIds.includes(c.id));

  function submit() {
    if (!selectedId) {
      toast.error("Select a user to add");
      return;
    }
    const vars =
      side === "vendor"
        ? { hubspotDealId, side: "vendor" as const, userId: selectedId }
        : {
            hubspotDealId,
            side: "partner" as const,
            partnerUserId: selectedId,
          };
    add.mutate(vars, {
      onSuccess: () => {
        setOpen(false);
        setSelectedId(null);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="mt-2 h-7 w-full text-[11px]"
        >
          <Plus className="mr-1 h-3 w-3" />
          Add {side === "vendor" ? "Bookboost staff" : "partner user"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Add {side === "vendor" ? "Bookboost staff" : "partner user"}
          </DialogTitle>
          <DialogDescription>
            {side === "vendor"
              ? "Pick a Bookboost team member to add as a collaborator on this deal."
              : "Pick a user from the attributed partner's organisation."}
          </DialogDescription>
        </DialogHeader>

        {eligible.isLoading ? (
          <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <p className="rounded-md border border-dashed bg-muted/30 px-3 py-6 text-center text-xs text-muted-foreground">
            {side === "vendor"
              ? "All Bookboost staff are already collaborators on this deal."
              : "No eligible partner users — make sure the attributed partner has portal access set up."}
          </p>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="collab" className="text-xs">
              {side === "vendor" ? "Team member" : "Partner user"}
            </Label>
            <select
              id="collab"
              value={selectedId ?? ""}
              onChange={(e) => setSelectedId(e.target.value || null)}
              className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
            >
              <option value="">Select…</option>
              {filtered.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name ?? "(unnamed)"}
                  {c.email ? ` — ${c.email}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setOpen(false);
              setSelectedId(null);
            }}
            disabled={add.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={add.isPending || !selectedId}
          >
            {add.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

